import { useState } from 'react'
import type { CreateOperatingCostEntryInput } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostCategory } from '@/types/api'
import {
  displayOperatingCostCategoryName,
  formatCategoryTotal,
  formatOperatingCostAmount,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'
import { OperatingCostEntryRow } from './OperatingCostEntryRow'
import { OperatingCostEntryForm } from './OperatingCostEntryForm'

type OperatingCostCategoryCardProps = {
  category: OperatingCostCategory
  isSaving: boolean
  onRename: (categoryId: number, name: string) => Promise<boolean>
  onDeleteCategory: (categoryId: number) => Promise<boolean>
  onCreateEntry: (
    categoryId: number,
    input: CreateOperatingCostEntryInput
  ) => Promise<boolean>
  onUpdateEntry: (
    entryId: number,
    input: CreateOperatingCostEntryInput
  ) => Promise<boolean>
  onDeleteEntry: (entryId: number) => Promise<boolean>
}

export function OperatingCostCategoryCard({
  category,
  isSaving,
  onRename,
  onDeleteCategory,
  onCreateEntry,
  onUpdateEntry,
  onDeleteEntry,
}: OperatingCostCategoryCardProps) {
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(category.name)
  const [pendingCategoryDelete, setPendingCategoryDelete] = useState(false)
  const [monthFilter, setMonthFilter] = useState('all')
  const isPercent = category.value_type === 'percent'
  const months = [
    ...new Set(
      category.entries
        .map((entry) => entry.competence_month)
        .filter((month): month is string => !!month)
    ),
  ]
    .sort()
    .reverse()
  const effectiveMonthFilter =
    monthFilter === 'undated' || months.includes(monthFilter) ? monthFilter : 'all'
  const entries = category.entries.filter(
    (entry) =>
      effectiveMonthFilter === 'all' ||
      (effectiveMonthFilter === 'undated'
        ? !entry.competence_month
        : entry.competence_month === effectiveMonthFilter)
  )

  return (
    <article
      className={styles.categoryCard}
      aria-labelledby={`operating-cost-category-${category.id}`}
    >
      <header className={styles.categoryHeader}>
        <div className={styles.categoryHeading}>
          {isRenaming && !category.is_system ? (
            <form
              className={styles.renameForm}
              onSubmit={(event) => {
                event.preventDefault()
                const trimmed = renameValue.trim()
                if (trimmed === '') return
                void onRename(category.id, trimmed).then((ok) => {
                  if (ok) setIsRenaming(false)
                })
              }}
            >
              <label className={styles.srOnly} htmlFor={`rename-category-${category.id}`}>
                Novo nome
              </label>
              <input
                id={`rename-category-${category.id}`}
                className={styles.input}
                value={renameValue}
                maxLength={80}
                disabled={isSaving}
                onChange={(event) => setRenameValue(event.target.value)}
              />
              <button
                type="submit"
                className={styles.secondaryButton}
                disabled={isSaving}
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                disabled={isSaving}
                onClick={() => {
                  setIsRenaming(false)
                  setRenameValue(category.name)
                }}
              >
                Cancelar
              </button>
            </form>
          ) : (
            <>
              <h3
                id={`operating-cost-category-${category.id}`}
                className={styles.categoryTitle}
              >
                {displayOperatingCostCategoryName(category)}
              </h3>
              {category.is_system ? (
                <span className={styles.badge}>Padrão</span>
              ) : (
                <span className={styles.badgeMuted}>Personalizada</span>
              )}
            </>
          )}
        </div>
        <p className={styles.categoryTotal}>{formatCategoryTotal(category)}</p>
      </header>

      {!category.is_system && !isRenaming ? (
        <div className={styles.categoryActions}>
          <button
            type="button"
            className={styles.ghostButton}
            disabled={isSaving}
            onClick={() => {
              setRenameValue(category.name)
              setIsRenaming(true)
              setPendingCategoryDelete(false)
            }}
          >
            Renomear
          </button>
          {pendingCategoryDelete ? (
            <>
              <button
                type="button"
                className={styles.dangerButton}
                disabled={isSaving}
                onClick={() => {
                  void onDeleteCategory(category.id)
                }}
              >
                {isSaving ? 'Excluindo...' : 'Confirmar exclusão'}
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                disabled={isSaving}
                onClick={() => setPendingCategoryDelete(false)}
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.ghostButton}
              disabled={isSaving}
              onClick={() => setPendingCategoryDelete(true)}
            >
              Excluir categoria
            </button>
          )}
        </div>
      ) : null}

      {!isPercent ? (
        <div className={styles.entryFilter}>
          <label className={styles.field}>
            Filtrar competência
            <select
              className={styles.input}
              value={effectiveMonthFilter}
              onChange={(event) => setMonthFilter(event.target.value)}
            >
              <option value="all">Todos os meses</option>
              <option value="undated">Sem competência</option>
              {months.map((month) => (
                <option key={month} value={month}>
                  {month.split('-').reverse().join('/')}
                </option>
              ))}
            </select>
          </label>
          <p className={styles.entryFormHint}>
            {entries.length} lançamento(s) •{' '}
            {formatOperatingCostAmount(
              entries.reduce(
                (total, entry) => total + Math.round((entry.amount ?? 0) * 100),
                0
              ) / 100
            )}{' '}
            no filtro
          </p>
        </div>
      ) : null}
      {entries.length === 0 ? (
        <p className={styles.empty}>Nenhum lançamento no filtro selecionado.</p>
      ) : (
        <ul className={styles.entryList}>
          {entries.map((entry) => (
            <li key={entry.id}>
              <OperatingCostEntryRow
                category={category}
                entry={entry}
                isSaving={isSaving}
                onUpdate={onUpdateEntry}
                onDelete={onDeleteEntry}
                onDuplicate={(input) => onCreateEntry(category.id, input)}
              />
            </li>
          ))}
        </ul>
      )}

      <OperatingCostEntryForm
        category={category}
        isSaving={isSaving}
        onSubmit={(input) => onCreateEntry(category.id, input)}
      />
      <p className={styles.entryFormHint}>
        Nomes podem se repetir, inclusive no mesmo mês. Cada lançamento soma seu próprio
        valor. O total da categoria reúne todos os meses cadastrados, incluindo futuros; o
        dashboard gerencial usa a competência de cada mês.
      </p>
    </article>
  )
}
